import React, { useState, useEffect } from 'react';
import { loadClients } from '../utils/storage';

const TaskView = () => {
  const [error, setError] = useState(null);
  const [taskData, setTaskData] = useState(null);

  useEffect(() => {
    const loadTaskData = async () => {
      try {
        // Obtener el ID de la tarea de los parámetros de la URL
        const searchParams = new URLSearchParams(window.location.search);
        const taskId = searchParams.get('taskId');
        
        if (!taskId) {
          setError('No se proporcionó un ID de tarea válido');
          return;
        }

        // Extraer los IDs de la tarea
        const [clientId, meetingId, taskId2] = taskId.split('-').map(Number);

        // Obtener los clientes desde la API
        const clients = await loadClients();
        const client = clients.find(c => c.id === clientId);
        
        if (!client) {
          setError('Cliente no encontrado');
          return;
        }

        const meeting = client.meetings.find(m => m.id === meetingId);
        if (!meeting) {
          setError('Reunión no encontrada');
          return;
        }

        const task = meeting.tasks.find(t => t.id === taskId2);
        if (!task) {
          setError('Tarea no encontrada');
          return;
        }

        // Combinar la información de la tarea con datos del cliente y la reunión
        const fullTaskData = {
          ...task,
          clientId,
          clientName: client.name,
          meetingId,
          meetingDate: meeting.date,
          meetingType: meeting.meetingType
        };

        setTaskData(fullTaskData);
      } catch (err) {
        setError('Ocurrió un error al cargar los detalles de la tarea');
        console.error('Error al cargar la tarea:', err);
      }
    };

    loadTaskData();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-[#0061A8] mb-6">Error al cargar la tarea</h1>
          <p className="text-[#63666A] mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-[#0061A8] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
          >
            Cerrar ventana
          </button>
        </div>
      </div>
    );
  }

  if (!taskData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0061A8]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-[#0061A8] mb-6">Detalles de la Tarea</h1>
        
        <div className="space-y-4">
          <div className="bg-[#F8F9FA] p-4 rounded-lg">
            <h2 className="text-lg font-semibold text-[#0061A8] mb-2">Descripción</h2>
            <p className="text-[#63666A]">{taskData.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#F8F9FA] p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-[#0061A8] mb-2">Información del Cliente</h2>
              <p className="text-[#63666A]">Cliente: {taskData.clientName}</p>
              <p className="text-[#63666A]">Asignado a: {taskData.assignedTo || 'N/A'}</p>
            </div>

            <div className="bg-[#F8F9FA] p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-[#0061A8] mb-2">Fechas</h2>
              <p className="text-[#63666A]">Fecha Límite: {taskData.dueDate ? new Date(taskData.dueDate).toLocaleDateString('es-MX') : 'N/A'}</p>
              <p className="text-[#63666A]">Fecha de Junta: {new Date(taskData.meetingDate).toLocaleDateString('es-MX')}</p>
            </div>
          </div>

          <div className="bg-[#F8F9FA] p-4 rounded-lg">
            <h2 className="text-lg font-semibold text-[#0061A8] mb-2">Detalles de la Junta</h2>
            <p className="text-[#63666A]">Tipo de Junta: {taskData.meetingType || 'N/A'}</p>
          </div>

          {taskData.completed && (
            <div className="bg-[#F8F9FA] p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-[#0061A8] mb-2">Estado de Completado</h2>
              <p className="text-[#63666A]">Comentario: {taskData.completionComment || 'Sin comentarios'}</p>
            </div>
          )}

          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={() => window.close()}
              className="bg-[#63666A] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Cerrar
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                `Tarea: ${taskData.description}\nCliente: ${taskData.clientName}\nFecha Límite: ${
                  taskData.dueDate ? new Date(taskData.dueDate).toLocaleDateString('es-MX') : 'N/A'
                }\nAsignado a: ${taskData.assignedTo || 'N/A'}\n\nVer detalles: ${window.location.href}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Compartir por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskView;
